import React, { useEffect, useMemo, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { getAssetAllocations } from "../api/hrmApi";
import { normalizeAssetAllocations } from "../api/hrmNormalizers";
import ComponentLoader from "../components/ComponentLoader";
import ScreenHeader from "./ScreenHeader";
import ScreenMetric from "./ScreenMetric";

const getStatusTone = (status = "") => {
  const normalizedStatus = String(status || "").toLowerCase();
  if (["assigned", "active", "issued"].some((value) => normalizedStatus.includes(value))) {
    return "approved";
  }
  if (["repair", "damaged", "lost", "overdue"].some((value) => normalizedStatus.includes(value))) {
    return "rejected";
  }
  return "pending";
};

const isLaptopAsset = (asset) =>
  [asset.category, asset.name, asset.assetTag].some((value) =>
    String(value || "").toLowerCase().includes("laptop")
  );

const AssetsScreen = ({
  assets = [],
  loading = false,
  loadingLabel = "Loading assets",
  onAction,
  onAssetsLoaded,
  viewUserId = "",
}) => {
  const [assetRows, setAssetRows] = useState(assets);
  const [assetError, setAssetError] = useState("");
  const [assetSearch, setAssetSearch] = useState("");
  const [assetLoading, setAssetLoading] = useState(false);
  const visibleAssets = useMemo(() => {
    const query = assetSearch.trim().toLowerCase();
    return [...assetRows]
      .sort((left, right) => {
        const laptopOrder = Number(!isLaptopAsset(left)) - Number(!isLaptopAsset(right));
        if (laptopOrder) {
          return laptopOrder;
        }
        return String(left.assetTag || left.name).localeCompare(String(right.assetTag || right.name));
      })
      .filter((asset) =>
        !query ||
        [asset.assetTag, asset.name, asset.category, asset.serialNumber, asset.status].some((value) =>
          String(value || "").toLowerCase().includes(query)
        )
      );
  }, [assetRows, assetSearch]);

  const summary = useMemo(() => {
    const assignedCount = assetRows.filter((asset) =>
      ["assigned", "active", "issued"].some((status) => String(asset.status || "").toLowerCase().includes(status))
    ).length;
    const categories = new Set(assetRows.map((asset) => asset.category).filter(Boolean)).size;
    const returnTrackedCount = assetRows.filter((asset) => {
      const returnDue = String(asset.returnDue || "").trim().toLowerCase();
      return returnDue && returnDue !== "on exit" && returnDue !== "-";
    }).length;

    return { assignedCount, categories, returnTrackedCount };
  }, [assetRows]);
  const isLoading = loading || assetLoading;

  useEffect(() => {
    setAssetRows(assets);
  }, [assets]);

  useEffect(() => {
    let isActive = true;

    const loadAssets = async () => {
      setAssetLoading(true);
      setAssetError("");
      try {
        const payload = await getAssetAllocations(viewUserId);
        const nextAssets = normalizeAssetAllocations(payload);
        if (!isActive) {
          return;
        }
        setAssetRows(nextAssets);
        onAssetsLoaded?.(nextAssets);
      } catch (error) {
        if (isActive) {
          setAssetError(error.message || "Assets could not be loaded.");
        }
      } finally {
        if (isActive) {
          setAssetLoading(false);
        }
      }
    };

    loadAssets();

    return () => {
      isActive = false;
    };
  }, [onAssetsLoaded, viewUserId]);

  return (
    <section className="new-hrm-screen">
      <ScreenHeader
        eyebrow="Inventory"
        title="Assets"
        description="Allocated company assets from Salesforce."
        actions={(
          <button className="new-hrm-primary-button" type="button" onClick={() => onAction("assetRequest")}>
            <FiPlus aria-hidden="true" />
            <span>Raise Asset Request</span>
          </button>
        )}
      />

      <div className="new-hrm-screen-metrics">
        <ScreenMetric label="Allocated" value={assetRows.length} detail="Salesforce assets" />
        <ScreenMetric label="Active" value={summary.assignedCount} detail="Currently issued" />
        <ScreenMetric label="Categories" value={summary.categories} detail="Asset types" />
      </div>

      <section className="new-hrm-panel new-hrm-loader-host">
        <ComponentLoader active={isLoading} label={assetLoading ? "Loading assets" : loadingLabel} />
        <header className="new-hrm-panel__header new-hrm-panel__header--compact">
          <h2>Allocated Assets</h2>
          <label className="new-hrm-assets-search">
            <span>Search Assets</span>
            <input
              value={assetSearch}
              onChange={(event) => setAssetSearch(event.target.value)}
              placeholder="Search asset, serial, category"
            />
          </label>
        </header>
        {assetError && <p className="new-hrm-empty-state">{assetError}</p>}
        <div className="new-hrm-data-table new-hrm-data-table--assets" role="table" aria-label="Allocated assets">
          <div className="new-hrm-data-table__head" role="row">
            <span role="columnheader">Asset</span>
            <span role="columnheader">Category</span>
            <span role="columnheader">Serial No.</span>
            <span role="columnheader">Assigned On</span>
            <span role="columnheader">Return Due</span>
            <span role="columnheader">Condition</span>
            <span role="columnheader">Status</span>
          </div>
          {visibleAssets.map((asset) => (
            <div key={asset.id} className="new-hrm-data-row" role="row">
              <span
                role="cell"
                data-label="Asset"
                aria-label={`Asset: ${asset.name || "Not provided"}${asset.assetTag ? `, tag ${asset.assetTag}` : ""}`}
              >
                <strong>{asset.name}</strong>
                <small>{asset.assetTag}</small>
              </span>
              <span role="cell" data-label="Category" aria-label={`Category: ${asset.category || "Not provided"}`}>
                {asset.category || "-"}
              </span>
              <span role="cell" data-label="Serial No." aria-label={`Serial number: ${asset.serialNumber || "Not provided"}`}>
                {asset.serialNumber || "-"}
              </span>
              <span role="cell" data-label="Assigned On" aria-label={`Assigned on: ${asset.assignedOn || "Not provided"}`}>
                {asset.assignedOn || "-"}
              </span>
              <span role="cell" data-label="Return Due" aria-label={`Return due: ${asset.returnDue || "Not provided"}`}>
                {asset.returnDue || "-"}
              </span>
              <span role="cell" data-label="Condition" aria-label={`Condition: ${asset.condition || "Not provided"}`}>
                {asset.condition || "-"}
              </span>
              <em
                role="cell"
                data-label="Status"
                aria-label={`Status: ${asset.status || "Assigned"}`}
                className={`new-hrm-status new-hrm-status--${getStatusTone(asset.status)}`}
              >
                {asset.status || "Assigned"}
              </em>
            </div>
          ))}
          {!visibleAssets.length && !isLoading && !assetError && (
            <p className="new-hrm-empty-state">
              {assetSearch ? "No allocated assets match this search." : "No allocated assets found in Salesforce."}
            </p>
          )}
        </div>
      </section>
    </section>
  );
};

export default AssetsScreen;
